import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as api from '../../api';

const ORDER_CYR = ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'Й', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ь', 'Ю', 'Я'];
const ORDER_LAT = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

const BTN = "inline-flex items-center gap-1 rounded-lg border border-[#1f2638] bg-[#121825] px-2.5 py-1.5 text-xs hover:bg-[#141b2d] focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_GHOST = "inline-flex items-center gap-1 rounded-lg border border-transparent bg-transparent px-2.5 py-1.5 text-xs hover:bg-[#0e1320] disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_PRIMARY = "inline-flex items-center gap-1 rounded-lg border border-violet-600/40 bg-violet-600/20 px-2.5 py-1.5 text-xs hover:bg-violet-600/30 focus:ring-2 focus:ring-violet-600/60 disabled:opacity-60 disabled:cursor-not-allowed";
const CHIP = "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border";

function prettyType(t) {
  const v = (t || 'online').toString().trim().toLowerCase();
  return v === 'campaign' ? 'Кампания' : 'Онлайн заявка';
}

export default function Templates({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const originalByIdRef = useRef({});

  const active = useMemo(() => templates.find(t => t.id === activeId) || null, [templates, activeId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const cyrToLat = (cyr) => {
    const idx = ORDER_CYR.indexOf(cyr);
    if (idx >= 0 && idx < ORDER_LAT.length) return ORDER_LAT[idx];
    return ORDER_LAT[ORDER_LAT.length - 1];
  };
  const nextCyr = () => {
    const used = new Set((active?.sections || []).map(s => s.id));
    for (const l of ORDER_CYR) if (!used.has(l)) return l;
    return ORDER_CYR[ORDER_CYR.length - 1] + '*';
  };

  const isStructLocked = (tpl) => Boolean(tpl?.is_locked);
  const activeLocked = isStructLocked(active);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listTemplates();
      setTemplates(list);
      const byId = {};
      for (const t of list) byId[t.id] = JSON.parse(JSON.stringify(t));
      originalByIdRef.current = byId;
      setActiveId(prev => (prev ?? (list[0]?.id ?? null)));
    } catch (e) {
      console.error(e);
      setToast('Грешка при зареждане на темплейти.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const addTemplate = async () => {
    try {
      const doc = await api.createTemplate({
        name: 'Нов темплейт',
        description: '',
        type: 'online',
        sections: [],
        is_default: false,
      });
      setTemplates(prev => [doc, ...prev]);
      originalByIdRef.current[doc.id] = JSON.parse(JSON.stringify(doc));
      setActiveId(doc.id);
      setToast('Създаден е нов темплейт.');
    } catch (e) {
      console.error(e);
      setToast(e?.response?.data?.detail || 'Грешка при създаване.');
    }
  };

  const duplicateTemplate = async (id) => {
    const src = templates.find(t => t.id === id);
    if (!src) return;
    try {
      const doc = await api.createTemplate({
        name: (src.name || 'Темплейт') + ' (копие)',
        description: src.description || '',
        type: src.type || 'online',
        sections: src.sections || [],
        is_default: false,
      });
      setTemplates(prev => [doc, ...prev]);
      originalByIdRef.current[doc.id] = JSON.parse(JSON.stringify(doc));
      setActiveId(doc.id);
      setToast('Направено е копие на темплейта.');
    } catch (e) {
      console.error(e);
      setToast(e?.response?.data?.detail || 'Грешка при копиране.');
    }
  };

  const removeTemplate = async (id) => {
    const tpl = templates.find(t => t.id === id);
    if (isStructLocked(tpl)) {
      setToast('Този темплейт е заключен и не може да бъде изтрит.');
      setConfirmDeleteId(null);
      return;
    }
    try {
      await api.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      delete originalByIdRef.current[id];
      if (activeId === id) setActiveId(null);
      setToast('Темплейтът е изтрит.');
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Грешка при изтриване.';
      setToast(msg);
      if (e?.response?.status === 409) reload();
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const updateActiveLocal = (patch) => {
    setTemplates(prev => prev.map(t => t.id === activeId ? { ...t, ...patch } : t));
  };

  const addSection = () => {
    if (!active) return;
    if (activeLocked) { setToast('Структурата е заключена: не може да добавяш секции.'); return; }
    const idCyr = nextCyr();
    const canon = cyrToLat(idCyr);
    const newSec = { id: idCyr, canon, title: '', items: [] };
    updateActiveLocal({ sections: [...(active.sections || []), newSec] });
  };

  const updateSectionTitle = (idx, title) => {
    if (!active) return;
    const arr = [...(active.sections || [])];
    arr[idx] = { ...arr[idx], title };
    updateActiveLocal({ sections: arr });
  };

  const removeSection = (idx) => {
    if (activeLocked) { setToast('Структурата е заключена: секции не могат да се трият.'); return; }
    const arr = [...(active.sections || [])];
    arr.splice(idx, 1);
    updateActiveLocal({ sections: arr });
  };

  const reindexItems = (s) => (s.items || []).map((it, i) => ({ ...it, id: `${s.id}${i + 1}` }));

  const addItem = (sidx) => {
    if (activeLocked) { setToast('Структурата е заключена: критерии не могат да се добавят.'); return; }
    const arr = [...(active.sections || [])];
    const s = { ...arr[sidx] };
    const nextNum = (s.items?.length || 0) + 1;
    const newItem = { id: `${s.id}${nextNum}`, label: `Критерий ${nextNum}`, max: 1, standard: "", mistake: "" };
    s.items = [...(s.items || []), newItem];
    arr[sidx] = s;
    updateActiveLocal({ sections: arr });
  };

  const updateItem = (sidx, iidx, patch) => {
    const arr = [...(active.sections || [])];
    const s = { ...arr[sidx] };
    const its = [...(s.items || [])];
    const safePatch = activeLocked
      ? (({ label, standard, mistake }) => {
        const out = {};
        if (label !== undefined) out.label = label;
        if (standard !== undefined) out.standard = standard;
        if (mistake !== undefined) out.mistake = mistake;
        return out;
      })(patch)
      : patch;
    its[iidx] = { ...its[iidx], ...safePatch };
    s.items = reindexItems({ ...s, items: its });
    arr[sidx] = s;
    updateActiveLocal({ sections: arr });
  };

  const removeItem = (sidx, iidx) => {
    if (activeLocked) { setToast('Структурата е заключена: критерии не могат да се трият.'); return; }
    const arr = [...(active.sections || [])];
    const s = { ...arr[sidx] };
    const its = [...(s.items || [])];
    its.splice(iidx, 1);
    s.items = reindexItems({ ...s, items: its });
    arr[sidx] = s;
    updateActiveLocal({ sections: arr });
  };

  function buildLockedSectionsForSave(current, original) {
    if (!Array.isArray(current) || !Array.isArray(original)) return original || [];
    const origIndex = {};
    for (const os of original) {
      origIndex[os.id] = {
        title: os.title || '',
        canon: os.canon,
        items: (os.items || []).reduce((m, it) => {
          m[it.id] = { max: it.max, label: it.label, standard: it.standard, mistake: it.mistake };
          return m;
        }, {})
      };
    }
    const curBySecId = {};
    for (const cs of current) curBySecId[cs.id] = cs;

    const out = [];
    for (const os of original) {
      const curSec = curBySecId[os.id] || {};
      const title = (curSec.title ?? os.title) || '';
      const itemsOut = [];
      const curItemsById = (curSec.items || []).reduce((m, it) => {
        m[it.id] = it;
        return m;
      }, {});
      for (const [iid, meta] of Object.entries(origIndex[os.id].items)) {
        const cit = curItemsById[iid] || {};
        itemsOut.push({
          id: iid,
          label: (cit.label ?? meta.label) || '',
          max: meta.max,
          standard: (cit.standard ?? meta.standard) || '',
          mistake: (cit.mistake ?? meta.mistake) || '',
        });
      }
      out.push({ id: os.id, canon: os.canon, title, items: itemsOut });
    }
    return out;
  }

  const saveActive = async () => {
    if (!active) return;
    try {
      let sectionsToSave = active.sections || [];
      if (activeLocked) {
        const original = originalByIdRef.current[active.id]?.sections || [];
        sectionsToSave = buildLockedSectionsForSave(active.sections, original);
      }
      const patch = {
        name: active.name,
        description: active.description,
        type: active.type,
        sections: sectionsToSave,
      };
      const updated = await api.updateTemplate(active.id, patch);
      setTemplates(prev => prev.map(t => t.id === active.id ? updated : t));
      originalByIdRef.current[active.id] = JSON.parse(JSON.stringify(updated));
      setToast('Записано.');
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Грешка при запис.';
      setToast(msg);
      if (e?.response?.status === 409) {
        reload();
      }
    }
  };

  const makeDefault = async () => {
    if (!active || active.is_default) return;
    try {
      const res = await api.setDefaultTemplate(active.id);
      await reload();
      setActiveId(res.id);
      setToast('Маркиран като дефолтен.');
    } catch (e) {
      console.error(e);
      setToast(e?.response?.data?.detail || 'Грешка при промяна на дефолтен.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed right-4 top-4 z-50">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-600/15 px-3 py-2 text-sm text-emerald-200 shadow-lg">
            {toast}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <h1 className="text-xl font-bold">Темплейти</h1>
        <button className={BTN_PRIMARY} onClick={addTemplate} disabled={loading}>+ Нов</button>
        {typeof onBack === 'function' && (
          <button className={BTN_GHOST} onClick={onBack}>← Назад</button>
        )}
        <div className="ml-auto text-xs text-slate-500">{loading ? 'зареждане…' : ''}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
        <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/50">
          <div className="p-3 text-sm text-slate-400">Запазени темплейти</div>
          <div className="divide-y divide-[#1f2638]">
            {templates.map(t => {
              const locked = isStructLocked(t);
              const isActive = activeId === t.id;
              return (
                <div
                  key={t.id}
                  className={`p-3 cursor-pointer hover:bg-[#0e1320] ${isActive ? 'bg-[#0e1320]' : ''}`}
                  onClick={() => setActiveId(t.id)}
                >

                  <div className="font-semibold text-slate-200 truncate">
                    {t.name || 'Без име'}
                  </div>

                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`${CHIP} bg-indigo-600/10 border-indigo-500/30 text-indigo-300`}>
                      {prettyType(t.type)}
                    </span>
                    {t.is_default && (
                      <span className={`${CHIP} bg-emerald-600/15 border-emerald-500/30 text-emerald-300`}>default</span>
                    )}
                    {locked ? (
                      <span className={`${CHIP} bg-rose-600/10 border-rose-500/30 text-rose-300`} title="Структурата е заключена">

                        <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z" />
                        </svg>

                        заключен
                      </span>
                    ) : (
                      <span className={`${CHIP} bg-amber-600/10 border-amber-500/30 text-amber-300`} title="Може да редактираш структурата">

                        <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z" />
                        </svg>

                        отключен
                      </span>
                    )}
                  </div>

                  {t.description && <div className="text-xs text-slate-500 truncate mt-1">{t.description}</div>}

                  <div className="mt-2 flex gap-2 items-center">
                    <button
                      className={BTN_GHOST}
                      onClick={(e) => { e.stopPropagation(); duplicateTemplate(t.id); }}
                    >
                      Дублирай
                    </button>

                    {!locked && (
                      <>
                        {confirmDeleteId === t.id ? (
                          <>
                            <button
                              className={`${BTN_GHOST} text-rose-300`}
                              onClick={(e) => { e.stopPropagation(); removeTemplate(t.id); }}
                            >
                              Потвърди
                            </button>
                            <button
                              className={BTN_GHOST}
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            >
                              Отказ
                            </button>
                          </>
                        ) : (
                          <button
                            className={`${BTN_GHOST} text-rose-300`}
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(t.id); }}
                          >
                            Изтрий
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {templates.length === 0 && (
              <div className="p-3 text-sm text-slate-500">Няма темплейти. Създай нов.</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-4">
          {!active ? (
            <div className="text-slate-400">Избери темплейт отляво или създай нов.</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-sm text-slate-400">Име на темплейта</label>
                  <input
                    className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                    value={active.name}
                    onChange={e => updateActiveLocal({ name: e.target.value })}
                    placeholder="напр. Кампания – стандарт 2025"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400">Тип темплейт</label>
                  <select
                    className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                    value={active.type || 'online'}
                    onChange={e => updateActiveLocal({ type: e.target.value })}
                    disabled={activeLocked}
                    title={activeLocked ? 'Заключен: типът не може да се променя' : 'Тип на темплейта'}
                  >
                    <option value="online">Онлайн заявка</option>
                    <option value="campaign">Кампания</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Дефолтен</label>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {active.is_default ? (
                      <>
                        <span className={`${CHIP} bg-emerald-600/15 border-emerald-500/30 text-emerald-300`}>default</span>
                        <span className="text-xs text-slate-500">Текущ дефолтен за {prettyType(active.type).toLowerCase()}.</span>
                      </>
                    ) : (
                      <button className={BTN} onClick={makeDefault}>
                        Направи дефолт
                      </button>
                    )}
                    {activeLocked ? (
                      <span className={`${CHIP} bg-rose-600/10 border-rose-500/30 text-rose-300`} title="Структурата е заключена">

                        <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z" />
                        </svg>


                        заключен
                      </span>
                    ) : (
                      <span className={`${CHIP} bg-amber-600/10 border-amber-500/30 text-amber-300`} title="Чернова (структурата може да се редактира)">

                        <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z" />
                        </svg>

                        отключен
                      </span>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-4">
                  <label className="text-sm text-slate-400">Описание</label>
                  <input
                    className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                    value={active.description || ''}
                    onChange={e => updateActiveLocal({ description: e.target.value })}
                    placeholder="кратка бележка за употребата"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex gap-2">
                  <button className={BTN_PRIMARY} onClick={saveActive}>Запази</button>
                </div>
                <button className={BTN_PRIMARY} onClick={addSection} disabled={activeLocked}>
                  + Нова секция
                </button>
              </div>

              <div className="mt-3 space-y-4">
                {(active.sections || []).map((s, sidx) => (
                  <div key={`${s.id}-${sidx}`} className="rounded-xl border border-[#1f2638] bg-[#0e1320] p-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-lg bg-[#141b2d] border border-[#1f2638] text-xs text-slate-300">{s.id}</span>
                        <span className="px-2 py-0.5 rounded-lg bg-[#141b2d] border border-[#1f2638] text-[11px] text-slate-400">{s.canon}</span>
                      </div>

                      <div className="flex-1 min-w-[220px]">
                        <label className="text-xs text-slate-400">Заглавие</label>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="text-sm text-slate-400">{s.id})</div>
                          <input
                            className="flex-1 bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                            value={s.title}
                            onChange={e => updateSectionTitle(sidx, e.target.value)}
                            placeholder="Начало на разговор"
                          />
                        </div>
                      </div>

                      <div className="ml-auto">
                        {!activeLocked && (
                          <button
                            className={`${BTN_GHOST} text-rose-300`}
                            onClick={() => removeSection(sidx)}
                          >
                            Изтрий
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="w-full mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-slate-400">Критерии</div>
                        <button className={BTN_GHOST} onClick={() => addItem(sidx)} disabled={activeLocked}>
                          + Добави критерий
                        </button>
                      </div>

                      <div className="space-y-4">
                        {(s.items || []).map((it, iidx) => (
                          <div key={`${it.id}-${iidx}`} className="space-y-2">
                            <div className="grid grid-cols-[70px,1fr,120px,auto] gap-2">
                              <div className="text-xs text-slate-400 grid items-center">
                                <div className="px-2 py-1 rounded-lg bg-[#121825] border border-[#1f2638] text-center">{it.id}</div>
                              </div>

                              <input
                                className="bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none"
                                value={it.label}
                                onChange={e => updateItem(sidx, iidx, { label: e.target.value })}
                                placeholder="Описание на критерия…"
                              />

                              <div className="relative">
                                <input
                                  type="number" min={0}
                                  className="w-full bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none pr-7"
                                  value={it.max}
                                  onChange={e => {
                                    const v = Math.max(0, Number(e.target.value || 0));
                                    updateItem(sidx, iidx, { max: v });
                                  }}
                                  disabled={activeLocked}
                                  title={activeLocked ? 'Заключено: точките (max) не могат да се променят' : 'Максимум точки'}
                                />
                                {activeLocked && (
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">

                                    <svg className="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z" />
                                    </svg>


                                  </span>
                                )}
                              </div>

                              {!activeLocked && (
                                <button
                                  className={`${BTN_GHOST} text-rose-300`}
                                  onClick={() => removeItem(sidx, iidx)}
                                >
                                  Изтрий
                                </button>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-slate-400">Стандарт</label>
                                <textarea
                                  rows={3}
                                  className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none"
                                  value={it.standard || ""}
                                  onChange={e => updateItem(sidx, iidx, { standard: e.target.value })}
                                  placeholder="Опиши стандарта за този критерий…"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-400">Грешка</label>
                                <textarea
                                  rows={3}
                                  className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-lg px-2 py-1 text-sm outline-none"
                                  value={it.mistake || ""}
                                  onChange={e => updateItem(sidx, iidx, { mistake: e.target.value })}
                                  placeholder="Какво се счита за грешка…"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        {(!s.items || s.items.length === 0) && (
                          <div className="text-xs text-slate-500">Няма критерии. Добави първия.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {(active.sections || []).length === 0 && (
                  <div className="rounded-lg border border-dashed border-[#1f2638] p-6 text-center text-slate-500">
                    Няма секции. Добави първата.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
